import {
  Control,
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormRegisterReturn,
  UseFormWatch,
} from 'react-hook-form';

import { User } from 'types/api/users.type';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { CheckboxWithControl } from 'components/CheckboxWithControl';
import { Headline } from 'components/Headline';
import { Input } from 'components/Input';
import { Label } from 'components/Label';
import { PostCodeSelect } from 'components/PostCodeSelect';
import { SelectWithControl } from 'components/SelectWithControl';
import { TextArea } from 'components/TextArea';

import * as S from './EditForm.styles';
import { FormItem } from './types';

type EditFormProps = {
  data: FormItem[];
  onSubmit: () => void;
  register: (name: string, options?: RegisterOptions) => UseFormRegisterReturn;
  control?: Control;
  isDirty?: boolean;
  isButtonsDisabled?: boolean;
  errors?: FieldErrors;
  reset?: (values: {}) => void;
  isPermissions?: boolean;
  initialValues?: User;
  submitBtnLabel?: string;
  children?: React.ReactNode;
  onPermissionsChange?: (arg: React.ChangeEvent<HTMLInputElement>, name?: string) => void;
  watch?: UseFormWatch<FieldValues>;
  hasButtons?: boolean;
  advancedValidation?: any;
  hideUndo?: boolean;
  buttonsExtra?: React.ReactNode;
};

export function EditForm(props: EditFormProps): React.ReactElement {
  const {
    data,
    onSubmit,
    register,
    control = undefined,
    isDirty = false,
    isButtonsDisabled = false,
    errors = {},
    reset = () => {},
    isPermissions = false,
    initialValues = {},
    submitBtnLabel = '',
    children = undefined,
    onPermissionsChange = undefined,
    watch = undefined,
    hasButtons = false,
    hideUndo = false,
    buttonsExtra = undefined,
  } = props;

  const isError = errors && Object.keys(errors).length !== 0;

  const mapDataToFields = data?.map((item: FormItem) => {
    if (item.type === 'postCodeSelect' && control && item.options) {
      return (
        <PostCodeSelect
          controlName={item.name}
          key={item.name}
          control={control}
          options={item.options}
          defaultValue={item.value as string}
          required={item.required}
          scale="s"
          isAdditionalTheme
        />
      );
    }

    if (item.type === 'select' && item.options && control) {
      return (
        <SelectWithControl
          motive="user"
          selectMotive="user"
          key={item.name}
          label={item.label}
          name={item.name}
          options={item.options}
          control={control}
          isMulti={item.isMulti}
          required={item.required}
          defaultValue={item.value}
          helperText={item.helperText}
        />
      );
    }

    if (item.type === 'select' && !item.options) {
      return null;
    }

    if (item.type === 'textbox') {
      return (
        <TextArea
          value={item.value as string | number}
          key={item.name}
          label={item.label}
          name={item.name}
          flag={item.flag}
          register={register}
          required={item.required}
        />
      );
    }

    if (item.type === 'checkbox' && item.label && control) {
      return (
        <CheckboxWithControl
          key={item.name}
          name={item.name}
          label={item.label}
          control={control}
          defaultChecked={item.defaultChecked}
          isCheckIcon={item.isCheckIcon}
          margin={item?.margin}
          disabled={item?.disabled}
          reset={reset}
          watch={watch}
          onCheck={
            onPermissionsChange
              ? (e, name) => {
                  onPermissionsChange(e, name);
                }
              : () => {
                  if (item.onChange) item.onChange();
                }
          }
        />
      );
    }

    if (item.type === 'checkboxWithLabel' && item.label && control) {
      return (
        <S.Wrapper key={item.name}>
          <Label>{item.label}</Label>
          <CheckboxWithControl
            key={item.name}
            name={item.name}
            label=""
            control={control}
            defaultChecked={!!item.value || item.defaultChecked}
            isCheckIcon={item.isCheckIcon}
            margin={item?.margin}
            disabled={item?.disabled}
            reset={reset}
            watch={watch}
            onCheck={
              onPermissionsChange
                ? (e, name) => {
                    onPermissionsChange(e, name);
                  }
                : () => {
                    if (item.onChange) item.onChange();
                  }
            }
          />
        </S.Wrapper>
      );
    }

    if (item.type === 'checkbox' && item.label && item.onChange) {
      return (
        <Checkbox
          onChange={item.onChange}
          key={item.name}
          name={item.name}
          label={item.label}
          defaultChecked={item.defaultChecked}
          hasBorderContainer={item.hasBorderContainer}
          margin={item?.margin}
          disabled={item?.disabled}
        />
      );
    }

    if (item.type === 'date') {
      return (
        <Input
          key={item.name}
          register={register}
          name={item.name}
          label={item.label}
          motive="m2"
          defaultValue={item.value as string | number}
          flag={item.flag}
          type="date"
          disabled={item.disabled}
          required={item.required}
        />
      );
    }

    if (item.type === 'file') {
      return (
        <Input
          key={item.name}
          register={register}
          name={item.name}
          label={item.label}
          type="file"
          motive="m2"
        />
      );
    }

    return (
      <Input
        key={item.name}
        register={register}
        name={item.name}
        label={item.label}
        disabled={item.disabled}
        motive="m2"
        flag={item.flag}
        required={item.required}
        options={{
          required: item.required && `${item.label?.toLocaleLowerCase()} is required`,
        }}
        error={errors && errors[item.name] && errors[item.name]?.message}
        defaultValue={item.value as string | number}
      />
    );
  });

  return (
    <form onSubmit={onSubmit}>
      <S.ContentContainer>
        <S.FieldContainer isPermissions={isPermissions}>{mapDataToFields}</S.FieldContainer>

        {children || null}

        {isError ? (
          <Headline scale="s" color="red">
            Looks like you have some issues, please check.
          </Headline>
        ) : null}

        {hasButtons && (
          <S.Buttons toCenter={hideUndo && !buttonsExtra}>
            {!hideUndo && (
              <Button
                onClick={() => {
                  if (reset && initialValues) {
                    reset(initialValues);
                  }
                }}
                type="button"
                disabled={!isDirty || isButtonsDisabled}
                motive="secondaryBlack"
                fontFamily="LFT Zeno Condensed"
                textTransform="uppercase"
                scale="base"
              >
                undo changes
              </Button>
            )}
            {buttonsExtra}
            <Button
              data-testid="submitbtn"
              type="submit"
              disabled={!isDirty || isButtonsDisabled}
              motive="primaryBlack"
              fontFamily="LFT Zeno Condensed"
              textTransform="uppercase"
              scale="base"
            >
              {submitBtnLabel || 'save changes'}
            </Button>
          </S.Buttons>
        )}
      </S.ContentContainer>
    </form>
  );
}
